import { useState } from 'react'
import {
  Modal,
  Stack,
  Switch,
  Group,
  Button,
  Text,
  Divider,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { usePmocGerarPdfV2 } from '../../hooks/usePmoc'
import { extractApiError } from '../../lib/extractApiError'
import type { PDFv2Flags } from '../../types/pmocPlanos'

interface Props {
  open: boolean
  onClose: () => void
  pmocId: string
}

const INICIAL: PDFv2Flags = {
  incluir_planos_padrao: true,
  incluir_planos_individuais: true,
  incluir_cronograma: true,
  incluir_arts: true,
  incluir_ocorrencias: false,
  incluir_medicoes_esperadas: true,
  incluir_texto_template: true,
}

export default function GerarPdfV2Modal({ open, onClose, pmocId }: Props) {
  const [flags, setFlags] = useState<PDFv2Flags>(INICIAL)
  const pdf = usePmocGerarPdfV2(pmocId)

  async function handleBaixar() {
    try {
      await pdf.mutateAsync(flags)
      notifications.show({
        color: 'green',
        message: 'PDF gerado — download iniciado.',
      })
      onClose()
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha ao gerar PDF',
        message: extractApiError(err, 'Erro na geração do PDF.'),
      })
    }
  }

  function toggle(key: keyof PDFv2Flags) {
    setFlags((f) => ({ ...f, [key]: !f[key] }))
  }

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title="Gerar PDF do PMOC (v2)"
      size="md"
      centered
    >
      <Stack gap="sm">
        <Text size="xs" c="dimmed">
          Selecione quais seções incluir no PDF. As flags são opcionais — todas
          ativas por padrão exceto "Ocorrências" (que pode gerar PDFs longos).
        </Text>

        <Divider label="Seções do PDF" labelPosition="left" />

        <Switch
          label="Texto-template técnico"
          description="Bloco normativo e contexto do plano"
          checked={!!flags.incluir_texto_template}
          onChange={() => toggle('incluir_texto_template')}
        />
        <Switch
          label="Planos individuais (snapshots PlanoPMOC)"
          description="Planos aplicados especificamente a este PMOC"
          checked={!!flags.incluir_planos_individuais}
          onChange={() => toggle('incluir_planos_individuais')}
        />
        <Switch
          label="Catálogo padrão (PlanoModelo do sistema)"
          description="Lista do que existe no catálogo disponível"
          checked={!!flags.incluir_planos_padrao}
          onChange={() => toggle('incluir_planos_padrao')}
        />
        <Switch
          label="Cronograma anual consolidado"
          description="Grade 12 meses x planos"
          checked={!!flags.incluir_cronograma}
          onChange={() => toggle('incluir_cronograma')}
        />
        <Switch
          label="Medições esperadas (faixas/limites)"
          checked={!!flags.incluir_medicoes_esperadas}
          onChange={() => toggle('incluir_medicoes_esperadas')}
        />
        <Switch
          label="ARTs anexadas"
          checked={!!flags.incluir_arts}
          onChange={() => toggle('incluir_arts')}
        />
        <Switch
          label="Ocorrências (lista detalhada)"
          description="Inclui no PDF — pode gerar muitas páginas"
          checked={!!flags.incluir_ocorrencias}
          onChange={() => toggle('incluir_ocorrencias')}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose} disabled={pdf.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleBaixar}
            loading={pdf.isPending}
            data-testid="pdf-v2-baixar"
          >
            📄 Baixar PDF
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
